/**
 * Category ordering utilities for managing predefined category order
 */

// Predefined category order
const PREDEFINED_CATEGORY_ORDER = [
  'Python',
  'Linear Algebra',
  'Machine Learning',
  'Deep Learning',
  'NLP'
];

/**
 * Sorts categories with predefined categories first, then alphabetically
 * @param {Array} categories - Array of category objects with name and exercise_count
 * @returns {Array} Sorted array of categories
 */
export const sortCategoriesWithPredefined = (categories) => {
  if (!categories || categories.length === 0) {
    return [];
  }

  return [...categories].sort((a, b) => {
    const aOrder = getCategoryOrderValue(a.name);
    const bOrder = getCategoryOrderValue(b.name);

    // If both are predefined or both are not predefined
    if ((aOrder < Number.MAX_SAFE_INTEGER && bOrder < Number.MAX_SAFE_INTEGER) ||
        (aOrder === Number.MAX_SAFE_INTEGER && bOrder === Number.MAX_SAFE_INTEGER)) {

      // If both are predefined, sort by predefined order
      if (aOrder < Number.MAX_SAFE_INTEGER && bOrder < Number.MAX_SAFE_INTEGER) {
        return aOrder - bOrder;
      }

      // If both are not predefined, sort alphabetically (simple string comparison)
      return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
    }

    // One is predefined, one is not - predefined comes first
    return aOrder - bOrder;
  });
};

/**
 * Gets the predefined category order array
 * @returns {Array} Copy of predefined category order
 */
export const getPredefinedCategoryOrder = () => {
  return [...PREDEFINED_CATEGORY_ORDER];
};

/**
 * Sorts categories alphabetically by name
 * @param {Array} categories - Array of category objects
 * @returns {Array} Alphabetically sorted categories
 */
export const sortCategoriesAlphabetically = (categories) => {
  if (!categories || categories.length === 0) {
    return [];
  }

  return [...categories].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

/**
 * Gets the order value for a category (lower = higher priority)
 * @param {string} categoryName - Name of the category
 * @returns {number} Order value (0-based index for predefined, MAX_SAFE_INTEGER for others)
 */
export const getCategoryOrderValue = (categoryName) => {
  if (!categoryName || typeof categoryName !== 'string') {
    return Number.MAX_SAFE_INTEGER;
  }

  const index = PREDEFINED_CATEGORY_ORDER.indexOf(categoryName);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};